import { nextTick, reactive } from 'vue'

const fieldRegistry = new WeakMap()

function createErrorBag () {
  const items = reactive([])

  return {
    items,
    clear () {
      items.splice(0, items.length)
    },
    has (field) {
      return items.some((entry) => entry.field === field)
    },
    first (field) {
      const entry = items.find((item) => item.field === field)
      return entry ? entry.msg : ''
    },
    any () {
      return items.length > 0
    },
    collect () {
      return [...items]
    },
    remove (field) {
      for (let i = items.length - 1; i >= 0; i -= 1) {
        if (items[i].field === field) items.splice(i, 1)
      }
    }
  }
}

function parseRules (value) {
  if (value == null || value === false) return []
  if (typeof value === 'string') {
    return value.split('|').map((rule) => rule.trim()).filter(Boolean)
  }
  if (typeof value === 'object') {
    const rules = []
    for (const [rule, ruleValue] of Object.entries(value)) {
      if (ruleValue === false || ruleValue === null || ruleValue === '') continue
      if (rule === 'required' && ruleValue === true) {
        rules.push('required')
      } else if (ruleValue === true) {
        rules.push(rule)
      } else {
        rules.push(`${rule}:${ruleValue}`)
      }
    }
    return rules
  }
  return []
}

function getRuleName (rule) {
  const idx = rule.indexOf(':')
  return idx === -1 ? rule : rule.slice(0, idx)
}

function getRuleParam (rule) {
  const idx = rule.indexOf(':')
  return idx === -1 ? null : rule.slice(idx + 1)
}

function getFieldMeta (vm, fieldName) {
  const fields = fieldRegistry.get(vm) || []
  return fields.find((entry) => entry.name === fieldName)
}

function getFieldDisplayName (vm, fieldName) {
  return getFieldMeta(vm, fieldName)?.alias || fieldName
}

function runRule (rule, value, fieldName, instanceRules, vm) {
  const ruleName = getRuleName(rule)
  const param = getRuleParam(rule)

  if (ruleName === 'required') {
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'boolean') return value
    return value !== undefined && value !== null && String(value).trim() !== ''
  }

  // Non-required value rules skip validation when the value is empty.
  // Emptiness is only enforced by the `required` rule (matches vee-validate).
  const isEmptyValue = value === undefined || value === null || String(value).trim() === ''

  if (ruleName === 'email') {
    if (isEmptyValue) return true
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value))
  }

  if (ruleName === 'numeric') {
    return isEmptyValue || !Number.isNaN(Number(value))
  }

  if (ruleName === 'min') {
    return isEmptyValue || String(value).length >= Number(param)
  }

  if (ruleName === 'max') {
    return isEmptyValue || String(value).length <= Number(param)
  }

  if (ruleName === 'min_value') {
    return isEmptyValue || Number(value) >= Number(param)
  }

  if (ruleName === 'max_value') {
    return isEmptyValue || Number(value) <= Number(param)
  }

  const customRule = instanceRules[ruleName]
  if (customRule?.validate) {
    try {
      const result = customRule.validate.call(vm, value, [param], fieldName)
      return result === true || result === 1 || (typeof result === 'number' && result > 0)
    } catch (error) {
      console.warn(`Validation rule "${ruleName}" failed for "${fieldName}":`, error)
      return true
    }
  }

  return true
}

function getRuleMessage (ruleName, displayName, param, instanceRules) {
  const customRule = instanceRules[ruleName]
  if (customRule?.getMessage) {
    return customRule.getMessage(displayName, param != null ? [param] : [])
  }

  const defaults = {
    required: () => `The ${displayName} field is required.`,
    min_value: () => `The ${displayName} field must be ${param} or more.`,
    max_value: () => `The ${displayName} field must be ${param} or less.`,
    email: () => `The ${displayName} field must be a valid email.`,
    numeric: () => `The ${displayName} field may only contain numeric characters.`,
    min: () => `The ${displayName} field must be at least ${param} characters.`,
    max: () => `The ${displayName} field may not be greater than ${param} characters.`
  }

  if (defaults[ruleName]) {
    return defaults[ruleName]()
  }

  return `The ${displayName} field is invalid`
}

function validateFieldValue (fieldName, value, rules, bag, instanceRules, vm) {
  const displayName = getFieldDisplayName(vm, fieldName)

  for (let i = bag.items.length - 1; i >= 0; i -= 1) {
    if (bag.items[i].field === fieldName) bag.items.splice(i, 1)
  }

  for (const rule of rules) {
    if (!runRule(rule, value, fieldName, instanceRules, vm)) {
      const ruleName = getRuleName(rule)
      bag.items.push({
        field: fieldName,
        msg: getRuleMessage(ruleName, displayName, getRuleParam(rule), instanceRules),
        rule: ruleName
      })
      return false
    }
  }

  return true
}

function createValidator (bag, vm) {
  const instanceRules = {}

  const getFields = () => fieldRegistry.get(vm) || []

  return {
    extend (name, definition) {
      instanceRules[name] = definition
    },
    localize () {},
    reset () {
      bag.clear()
    },
    fields: {
      find ({ name }) {
        return {
          reset () {
            for (let i = bag.items.length - 1; i >= 0; i -= 1) {
              if (bag.items[i].field === name) bag.items.splice(i, 1)
            }
          }
        }
      }
    },
    validate (name) {
      const field = getFields().find((entry) => entry.name === name)
      if (!field) return Promise.resolve(true)
      const valid = validateFieldValue(
        field.name,
        field.getValue(),
        field.rules,
        bag,
        instanceRules,
        vm
      )
      return Promise.resolve(valid)
    },
    validateAll (values = null, extraValues = null) {
      bag.clear()
      let valid = true
      const fields = getFields()
      const valueMap = {
        ...(typeof values === 'object' && values ? values : {}),
        ...(typeof extraValues === 'object' && extraValues ? extraValues : {})
      }

      for (const field of fields) {
        const fieldValue = Object.prototype.hasOwnProperty.call(valueMap, field.name)
          ? valueMap[field.name]
          : field.getValue()

        if (!validateFieldValue(field.name, fieldValue, field.rules, bag, instanceRules, vm)) {
          valid = false
        }
      }

      return Promise.resolve(valid)
    },
    validateField (fieldName, value, rules = null) {
      const field = getFields().find((entry) => entry.name === fieldName)
      const fieldRules = rules || field?.rules || []
      return validateFieldValue(fieldName, value, fieldRules, bag, instanceRules, vm)
    }
  }
}

function registerField (vm, field) {
  if (!fieldRegistry.has(vm)) fieldRegistry.set(vm, [])
  const fields = fieldRegistry.get(vm)
  const index = fields.findIndex((entry) => entry.name === field.name)
  if (index === -1) {
    fields.push(field)
  } else {
    fields[index] = { ...fields[index], ...field }
  }
}

function unregisterField (vm, name, bag) {
  const fields = fieldRegistry.get(vm)
  if (fields) {
    const index = fields.findIndex((entry) => entry.name === name)
    if (index !== -1) fields.splice(index, 1)
  }
  // Drop any stale error for a field whose element is gone (e.g. hidden by skip logic)
  if (bag) {
    for (let i = bag.items.length - 1; i >= 0; i -= 1) {
      if (bag.items[i].field === name) bag.items.splice(i, 1)
    }
  }
}

function readElementValue (el) {
  if (!el) return undefined
  // Radio: the directive is attached to a single radio, but the answer belongs
  // to the whole group. Read the checked radio among all radios sharing the name
  // so an answered group (e.g. "No" selected) does not fail the required rule.
  if (el.type === 'radio') {
    const name = el.name || el.getAttribute('name')
    if (name && typeof document !== 'undefined') {
      const group = document.getElementsByName(name)
      for (let i = 0; i < group.length; i += 1) {
        if (group[i].checked) return group[i].value
      }
      return undefined
    }
    return el.checked ? el.value : undefined
  }
  // Checkbox: for a checkbox group, report the list of checked values so a group
  // with at least one selection passes required; a lone checkbox reports boolean.
  if (el.type === 'checkbox') {
    const name = el.name || el.getAttribute('name')
    if (name && typeof document !== 'undefined') {
      const group = Array.prototype.filter.call(
        document.getElementsByName(name),
        (node) => node.type === 'checkbox'
      )
      if (group.length > 1) {
        return group.filter((node) => node.checked).map((node) => node.value)
      }
    }
    return el.checked
  }
  return el.value
}

function readBoundValue (el, binding, vnode) {
  if (vnode?.props && Object.prototype.hasOwnProperty.call(vnode.props, 'modelValue')) {
    return vnode.props.modelValue
  }
  if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT')) {
    return readElementValue(el)
  }
  return binding.value
}

export function createVeeValidateV2CompatPlugin () {
  return {
    install (app) {
      app.config.globalProperties.$set = (target, key, value) => {
        target[key] = value
      }

      app.directive('validate', {
        mounted (el, binding, vnode) {
          const vm = binding.instance
          if (!vm || !vm.$validator || !vm.errors) return

          const name = binding.arg || el.getAttribute('name')
          if (!name) return

          const rules = parseRules(binding.value)
          const field = {
            name,
            alias: el.getAttribute('data-vv-as') || name,
            rules,
            getValue () {
              return readBoundValue(el, binding, vnode)
            }
          }

          registerField(vm, field)

          const run = () => {
            vm.$validator.validateField(name, field.getValue(), rules)
          }

          // Re-validate on input only when the field already has an error,
          // so a stale message clears as the user edits/clears the value
          // without showing new errors mid-typing.
          const runOnInput = () => {
            if (vm.errors?.has?.(name)) {
              vm.$validator.validateField(name, field.getValue(), rules)
            }
          }

          el.__vvRunValidation = run
          el.__vvRunOnInput = runOnInput
          el.__vvFieldName = name
          el.addEventListener('blur', run)
          el.addEventListener('change', run)
          el.addEventListener('input', runOnInput)

          if (binding.modifiers?.initial) {
            nextTick(() => {
              try {
                run()
              } catch (error) {
                console.warn(`Initial validation failed for "${name}":`, error)
              }
            })
          }
        },
        updated (el, binding, vnode) {
          const vm = binding.instance
          if (!vm) return

          const name = binding.arg || el.getAttribute('name')
          if (!name) return

          registerField(vm, {
            name,
            alias: el.getAttribute('data-vv-as') || name,
            rules: parseRules(binding.value),
            getValue () {
              return readBoundValue(el, binding, vnode)
            }
          })
        },
        unmounted (el, binding) {
          const vm = binding.instance
          const name = el.__vvFieldName || binding.arg || el.getAttribute('name')
          if (el.__vvRunValidation) {
            el.removeEventListener('blur', el.__vvRunValidation)
            el.removeEventListener('change', el.__vvRunValidation)
            delete el.__vvRunValidation
          }
          if (el.__vvRunOnInput) {
            el.removeEventListener('input', el.__vvRunOnInput)
            delete el.__vvRunOnInput
          }
          // When a field's element is removed from the DOM (e.g. a question hidden
          // by skip logic), unregister it so validateAll() no longer treats a
          // hidden required field as invalid and blocks submission.
          if (vm && name) {
            unregisterField(vm, name, vm.errors)
          }
          delete el.__vvFieldName
        }
      })

      app.mixin({
        beforeCreate () {
          const bag = createErrorBag()
          const validator = createValidator(bag, this)
          this.$validator = validator
          this.errors = bag
        },
        unmounted () {
          fieldRegistry.delete(this)
        }
      })
    }
  }
}
